import { css } from '@emotion/react';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  addMissingSourceColumnsToRows,
  addNewRow, createColumnIdFromNumber,
  createKeyFromRealSpreadsheetId,
  createRecordWithDefaultForMaps, createUsedColumnKeyForMap, extractRealSpreadsheetIdFromKey,
  getMapFromRealSpreadsheetId, getRowUsedOtherData, getSpreadsheetIdFromMapId, getTargetColumnForColumn,
  hasRowOnlyOneSourceMap, type IntersectsMapItem, type PerMapIdColumn, removeRow, type Row, type RowWithExtraData, selectColumn,
  spreadsheetMatchupColumnsToRealSpreadsheetIds,
} from 'resources/js/src/map/layered/createLayeredMapModal/MatchupColumnsSection/matchupColumnsSection.helpers';
import { type DropdownOption } from '~/_shared/baseComponents/dropdown';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  type MapId, type MapInfo,
} from '~/_shared/types/map';
import {
  type PerSpreadsheetMatchupData, type SpreadsheetMatchupColumns,
} from '~/_shared/types/spreadsheetData/matchupData';
import type {
  ColumnName,
  RealSpreadsheetColumn,
} from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import {
  compareRealSpreadsheetColumns,
  filterAddedSpreadsheetColumns, SERVER_ADDED_COLUMN_NAMES,
} from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { uniqueValues } from '~/_shared/utils/array/array.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  stringObjectEntries, stringObjectKeys,
} from '~/_shared/utils/object/objectEnumerators';
import {
  isNullsy,
  notNullsy,
  notUndefined,
} from '~/_shared/utils/typeGuards';
import {
  type ColumnIdPerRealSpreadsheet, type MapIntersect,
  type RealSpreadsheetResponseId,
} from '~/map/map.repository';

export type MatchupColumnDropdownOption = DropdownOption<RealSpreadsheetColumn | null>;

const getColumnDataByIntersectSpreadsheetKey = (props: {
  columnData?: PerSpreadsheetMatchupData;
  intersect: MapIntersect;
  maps: IntersectsMapItem[];
  spreadsheetKey: RealSpreadsheetResponseId;
}) => {
  const realSpreadsheetId = extractRealSpreadsheetIdFromKey(props.spreadsheetKey);
  const map = getMapFromRealSpreadsheetId(props.maps, realSpreadsheetId);

  if (map) {
    const columnId = props.intersect.intersects[props.spreadsheetKey];
    if (columnId) {
      const spreadsheetId = getSpreadsheetIdFromMapId(props.maps, map.id);
      if (spreadsheetId && realSpreadsheetId) {
        const name = props.columnData?.[spreadsheetId]?.columns?.[columnId];
        if (!isNullsy(name)) {
          return {
            id: columnId,
            realSpreadsheetId,
            name,
          };
        }
      }
    }
  }
  return null;
};

// Fills the passed sourceColumnsPerMap under the mapId key with column info extracted from map intersects.
const populateSourceColumnsMapWithColumnData = (props: {
  columnData?: PerSpreadsheetMatchupData;
  intersect: MapIntersect;
  maps: IntersectsMapItem[];
  sourceColumnsPerMap: PerMapIdColumn;
  spreadsheetKey: RealSpreadsheetResponseId;
}) => {
  const realSpreadsheetId = extractRealSpreadsheetIdFromKey(props.spreadsheetKey);
  const map = getMapFromRealSpreadsheetId(props.maps, realSpreadsheetId);

  if (map) {
    const columnId = props.intersect.intersects[props.spreadsheetKey];
    if (columnId) {
      const spreadsheetId = getSpreadsheetIdFromMapId(props.maps, map.id);
      if (spreadsheetId) {
        props.sourceColumnsPerMap[map.id] = getColumnDataByIntersectSpreadsheetKey({
          columnData: props.columnData,
          intersect: props.intersect,
          maps: props.maps,
          spreadsheetKey: props.spreadsheetKey,
        });
      }
    }
  }
};

type UseMatchupColumnsSectionProps = {
  maps: IntersectsMapItem[];
  onSubmit: (data: MapIntersect[]) => void;
  isLoading: boolean;
  intersects: MapIntersect[];
  columnData?: PerSpreadsheetMatchupData;

  getMapIdFromPrimarySpreadsheetId: (mapList: IntersectsMapItem[], spreadsheetId: number) => number | null;
  getMapFromRealSpreadsheetId: (mapList: IntersectsMapItem[], spreadsheetId: number) => IntersectsMapItem | null;
  getSpreadsheetIdFromMapId: (mapList: IntersectsMapItem[], mapId: number) => number | null;
  getRealSpreadsheetIdFromMapId: (mapList: IntersectsMapItem[], mapId: number) => number | null;
  getPrimarySpreadsheetIdFromMapId: (mapList: IntersectsMapItem[], mapId: number) => number | null;

  layeringMapInfo?: MapInfo | null;
};

export const useMatchupColumnsSection = (props: UseMatchupColumnsSectionProps) => {
  const {
    maps,
    onSubmit,
    isLoading,
    intersects: newIntersects,
    columnData,
    getMapIdFromPrimarySpreadsheetId,
    getMapFromRealSpreadsheetId,
    getSpreadsheetIdFromMapId,
    getPrimarySpreadsheetIdFromMapId,
    getRealSpreadsheetIdFromMapId,
    layeringMapInfo,
  } = props;

  const [t] = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);

  const isEditing = Boolean(layeringMapInfo?.layering);
  const isConnected = Boolean(layeringMapInfo?.layering?.connected);
  const sourceColumns = layeringMapInfo?.layering?.sourceColumns;

  const baseAvailableDropdownOptions: ReadonlyArray<MatchupColumnDropdownOption> = useMemo(() => ([{
    name: t('Unmatched'),
    value: null,
    style: (theme: Theme) => css({
      color: theme.textColors.disabled,
    }),
  }]), [t]);

  const createNullsPerMap = useCallback(() =>
    createRecordWithDefaultForMaps(maps, null),
  [maps]);

  const addToIntersectsAndUsedKeys = useCallback((props: {
    columns?: SpreadsheetMatchupColumns;
    intersectValue?: ColumnName | null;
    intersects: Record<RealSpreadsheetResponseId, ColumnName | null>;
    mapId: MapId;
    realSpreadsheetId?: number;
    usedKeys: Set<string>;
  }) => {
    const { intersects, intersectValue, mapId, usedKeys } = props;
    const realSpreadsheetId = props.realSpreadsheetId ?? getRealSpreadsheetIdFromMapId(maps, mapId);
    const primarySpreadsheetId = props.realSpreadsheetId ?? getPrimarySpreadsheetIdFromMapId(maps, mapId);
    if (realSpreadsheetId && primarySpreadsheetId) {
      const realSpreadsheetKey: string = createKeyFromRealSpreadsheetId(realSpreadsheetId);
      const columns = props.columns || columnData?.[primarySpreadsheetId]?.columns || {};
      const targetColumn = getTargetColumnForColumn({
        columnName: intersectValue || null, columns, mapId, usedKeys,
      });

      if (targetColumn) {
        usedKeys.add(createUsedColumnKeyForMap(mapId, targetColumn));
      }

      if (realSpreadsheetKey) {
        intersects[realSpreadsheetKey] = targetColumn;
      }
    }
  }, [columnData, getPrimarySpreadsheetIdFromMapId, getRealSpreadsheetIdFromMapId, maps]);

  const getDropdownOption = useCallback((realSpreadsheetColumn: RealSpreadsheetColumn | null): MatchupColumnDropdownOption => {
    const isNew = realSpreadsheetColumn && !!layeringMapInfo?.layering?.additional.new[realSpreadsheetColumn.realSpreadsheetId]?.[realSpreadsheetColumn.id];
    const displayName = realSpreadsheetColumn?.name || '';
    return {
      name: displayName,
      value: realSpreadsheetColumn,
      style: (theme: Theme) => css({
        color: isNew ? theme.textColors.info : theme.textColors.primary,
      }),
      suffix: isNew ? ` (${t('new')})` : undefined,
    };
  }, [layeringMapInfo?.layering?.additional.new, t]);

  const getAvailableTargetColumnsForBaseMapWithoutNone = useCallback((columnOptions: ReadonlyArray<RealSpreadsheetColumn | null>) => {
    const options: MatchupColumnDropdownOption[] = [];
    const valueSet: Set<string> = new Set<string>();

    const getColumnOptionHash = (value: RealSpreadsheetColumn): string => {
      return `${value.realSpreadsheetId}:${value.id}`;
    };

    Object.values(columnOptions).forEach(columnOption => {
      if (!columnOption) {
        return;
      }
      const isServerValue = SERVER_ADDED_COLUMN_NAMES.has(columnOption.name);
      const valueHash = getColumnOptionHash(columnOption);
      if (!valueSet.has(valueHash) && !isServerValue) {
        options.push(getDropdownOption(columnOption));
        valueSet.add(valueHash);
      }
    });

    options.sort((optionA, optionB) => {
      const lowerCaseNameA = optionA.name.toLowerCase();
      const lowerCaseNameB = optionB.name.toLowerCase();

      return lowerCaseNameA < lowerCaseNameB ? -1 : lowerCaseNameB < lowerCaseNameA ? 1 : 0;
    });

    return options;
  }, [getDropdownOption]);

  const getAvailableTargetColumnsForBaseMap = useCallback((columnOptions: ReadonlyArray<RealSpreadsheetColumn | null>) => (
    [...baseAvailableDropdownOptions, ...getAvailableTargetColumnsForBaseMapWithoutNone(columnOptions)]
  ), [baseAvailableDropdownOptions, getAvailableTargetColumnsForBaseMapWithoutNone]);

  const sourceColumnsPerMap: Record<MapId, MatchupColumnDropdownOption[]> = useMemo(() => {
    const options: Record<MapId, MatchupColumnDropdownOption[]> = {};
    for (const key in columnData) {
      if (columnData?.hasOwnProperty(key)) {
        const spreadsheetId: number = Number(key);
        const mapId = getMapIdFromPrimarySpreadsheetId(maps, spreadsheetId);
        const columns = columnData?.[spreadsheetId]?.columns || {};
        if (mapId) {
          const realSpreadsheetId = getRealSpreadsheetIdFromMapId(maps, mapId);
          if (realSpreadsheetId) {
            options[mapId] = getAvailableTargetColumnsForBaseMap(
              spreadsheetMatchupColumnsToRealSpreadsheetIds(realSpreadsheetId, columns),
            );
          }
        }
      }
    }
    if (isEditing) {
      for (const map of maps) {
        if (map.isCurrentMap && map.realSpreadsheetId) {
          const spreadsheetKey = createKeyFromRealSpreadsheetId(map.realSpreadsheetId);
          const columns = sourceColumns?.[spreadsheetKey] || {};
          options[map.id] = getAvailableTargetColumnsForBaseMap(
            spreadsheetMatchupColumnsToRealSpreadsheetIds(map.realSpreadsheetId, columns)
          );
        }
      }
    }
    return options;
  }, [columnData, getAvailableTargetColumnsForBaseMap, getMapIdFromPrimarySpreadsheetId, getRealSpreadsheetIdFromMapId, isEditing, maps, sourceColumns]);

  const onSourceColumnChange = useCallback((column: RealSpreadsheetColumn | null, mapId: number, index: number) => {
    setRows(selectColumn(rows, column, mapId, index, sourceColumnsPerMap, maps));
  }, [rows, sourceColumnsPerMap, maps]);

  const onTargetColumnNameChange = useCallback((value: string | null, index: number) => {
    const row = rows[index];
    if (!row) {
      return;
    }

    const newValue = value?.length ? value : null;

    let newOptions: Row[] = [...rows.slice(0, index), {
      ...row,
      targetColumnName: newValue,
    }, ...rows.slice(index + 1)];
    if (newValue) {
      const prevIndex = getRowUsedOtherData(rows, newValue, index);
      if (prevIndex !== -1) {
        const newOption = newOptions[prevIndex];
        if (!newOption) {
          return;
        }

        newOptions = [...newOptions.slice(0, prevIndex), {
          ...newOption,
          targetColumnName: null,
        }, ...newOptions.slice(prevIndex + 1)];
      }
    }
    setRows(newOptions);
  }, [rows]);

  const onRemoveRow = useCallback((index: number) => {
    setRows(rows => removeRow(rows, index, sourceColumnsPerMap, maps));
  }, [sourceColumnsPerMap, maps]);

  const onAddRow = useCallback(() => {
    if (isLoading) {
      return;
    }
    setRows(rows => addNewRow(rows, maps));
  }, [isLoading, maps]);

  const handleGenerateIntersectsAndSubmit = useCallback(() => {
    const usedKeys = new Set<string>();
    if (layeringMapInfo?.isLayered && !layeringMapInfo.layering?.connected) {
      const layeredMapColumns = layeringMapInfo.layering?.columns;
      const realSpreadsheetId = layeringMapInfo.spreadsheets[0]?.realSpreadsheets[0]?.realSpreadSheetId ?? 0;
      const layeredMapSpreadsheetKey = createKeyFromRealSpreadsheetId(realSpreadsheetId);
      const data: MapIntersect[] = rows
        .filter(row => row.isIncluded)
        .filter(combinedOption => combinedOption.targetColumnName)
        .map((combinedOption, index) => {
          const layeredMapColumnId = createColumnIdFromNumber(index);
          const newIntersects = {
            [layeredMapSpreadsheetKey]: layeredMapColumns?.hasOwnProperty(layeredMapColumnId)
              ? layeredMapColumnId
              : null,
          };
          maps.forEach(map => {
            const intersectValue = combinedOption.selectedSourceColumnPerMap[map.id];
            if (notNullsy(intersectValue) && !map.isCurrentMap) {
              addToIntersectsAndUsedKeys({
                mapId: map.id, intersectValue: intersectValue.name, intersects: newIntersects, usedKeys,
              });
            }
          });
          return {
            //the targetColumnName is a 100% there, as the values without it got filtered out
            name: combinedOption.targetColumnName || '',
            intersects: newIntersects,
          };
        });
      onSubmit(data);
    }
    else {
      const data: MapIntersect[] = rows
        .filter(row => row.isIncluded)
        .filter(combinedOption => combinedOption.targetColumnName)
        .map(combinedOption => {
          const intersects = combinedOption.selectedSourceColumnPerMap;
          const newIntersects: ColumnIdPerRealSpreadsheet = {};

          maps.forEach(map => {
            const intersectValueRaw = intersects[map.id];
            if (notUndefined(intersectValueRaw)) {
              const intersectValue = intersectValueRaw?.name || null;
              if (map.isCurrentMap && map.realSpreadsheetId) {
                const realSpreadsheetKey = createKeyFromRealSpreadsheetId(map.realSpreadsheetId);
                const columns = sourceColumns?.[realSpreadsheetKey] ?? {};
                addToIntersectsAndUsedKeys({
                  columns,
                  intersects: newIntersects,
                  intersectValue,
                  mapId: map.id,
                  usedKeys,
                  realSpreadsheetId: map.realSpreadsheetId,
                });
              }
              else {
                addToIntersectsAndUsedKeys({
                  mapId: map.id, intersectValue, intersects: newIntersects, usedKeys,
                });
              }
            }
          });

          return {
            name: combinedOption.targetColumnName || '',
            intersects: newIntersects,
          };
        });

      onSubmit(data);
    }
  },
  [
    layeringMapInfo?.isLayered, layeringMapInfo?.layering?.connected, layeringMapInfo?.layering?.columns,
    layeringMapInfo?.spreadsheets, rows, onSubmit, maps, addToIntersectsAndUsedKeys, sourceColumns],
  );

  // This calculates the rows in editing layered map mode
  useEffect(() => {
    if (isEditing) {
      const newRows: Row[] = [];
      const currentIntersects = filterAddedSpreadsheetColumns(layeringMapInfo?.layering?.matches) ?? [];
      const newIntersectsAdded: boolean[] = currentIntersects.map(() => false);
      let isRemovable = true;
      // These are the current LM intersects, generated with the initial POST LM call
      // or the last edit LM call. In this block we try to match the new intersects with
      // these ones, in order to match on the same rows old and new base maps
      currentIntersects.forEach((intersect, intersectIndex) => {
        if (intersect) {
          const spreadsheetKeys = Object.keys(intersect.intersects);
          const selectedSourceColumnPerMap: PerMapIdColumn = createNullsPerMap();
          let targetColumnName: string | null = null;
          for (const spreadsheetKey of spreadsheetKeys) {
            const realSpreadsheetId = extractRealSpreadsheetIdFromKey(spreadsheetKey);
            const map = getMapFromRealSpreadsheetId(maps, realSpreadsheetId);
            if (map) {
              targetColumnName = intersect.name;
              const columnId = intersect?.intersects?.[spreadsheetKey];
              if (columnId) {
                const name = sourceColumns?.[spreadsheetKey]?.[columnId];
                selectedSourceColumnPerMap[map.id] = isNullsy(name) ? null : {
                  id: columnId,
                  realSpreadsheetId,
                  name,
                };
              }
              const layeredMapColumnId = createColumnIdFromNumber(intersectIndex);
              const layeredMapSpreadsheetId = layeringMapInfo?.spreadsheets?.[0]?.realSpreadsheets?.[0]?.realSpreadSheetId ?? 0;
              const layeredMapRealSpreadsheetId = createKeyFromRealSpreadsheetId(layeredMapSpreadsheetId);

              // We add all the newly added maps or columns (if any) to the current intersects, when matching
              newIntersects.forEach((newIntersect, i) => {
                if (columnId === null) {
                  return;
                }
                let isNewIntersectsAdded = false;
                // If the LM is connected, the new intersects will be calculated based on current LM's spreadsheet
                if (isConnected) {
                // We search for the intersect in the new intersects that matches the same column
                // in the current layered map intersects, so we can merge the new maps/columns there
                  if (newIntersect?.intersects[spreadsheetKey] === columnId) {
                    isNewIntersectsAdded = true;
                  }
                }
                else {
                  if (newIntersect?.intersects[layeredMapRealSpreadsheetId] === layeredMapColumnId) {
                    isRemovable = false;
                    isNewIntersectsAdded = true;
                  }
                }

                if (isNewIntersectsAdded) {
                // We flag this new intersect as "added", this means that in this newIntersect there is at least
                // one spreadsheet (and associated map) that is already present in the current
                // layered map matches, so we'll attach the new spreadsheet keys into the same
                // intersect (in the sourceColumnsPerMap object).
                  newIntersectsAdded[i] = true;

                  const newIntersectRealSpreadsheetKeys = Object.keys(newIntersect.intersects);
                  for (const newIntersectRealSpreadsheetKey of newIntersectRealSpreadsheetKeys) {
                  // I ignore spreadsheets/maps that are already matched/layered in the layered map
                    if (!intersect.intersects[newIntersectRealSpreadsheetKey]) {
                      const newRealSpreadsheetId = extractRealSpreadsheetIdFromKey(newIntersectRealSpreadsheetKey);
                      const mapFromRealSpreadsheetId = getMapFromRealSpreadsheetId(maps, newRealSpreadsheetId);
                      if (mapFromRealSpreadsheetId) {
                        const newColumnId = newIntersect?.intersects?.[newIntersectRealSpreadsheetKey];
                        if (newColumnId) {
                          let columnName = null;
                          if (layeringMapInfo?.layering?.sourceColumns[newIntersectRealSpreadsheetKey]?.[newColumnId]) {
                            columnName = layeringMapInfo.layering.sourceColumns[newIntersectRealSpreadsheetKey][newColumnId];
                          }
                          else {
                            const newSpreadsheetId = getSpreadsheetIdFromMapId(maps, mapFromRealSpreadsheetId.id);
                            columnName = newSpreadsheetId ? columnData?.[newSpreadsheetId]?.columns?.[newColumnId] : null;
                          }
                          if (!isNullsy(columnName)) {
                            selectedSourceColumnPerMap[mapFromRealSpreadsheetId.id] = {
                              id: newColumnId,
                              realSpreadsheetId: newRealSpreadsheetId,
                              name: columnName,
                            };
                          }
                        }
                      }
                    }
                  }
                }
              });
            }
          }

          const newCombinedOption: Row = {
            selectedSourceColumnPerMap,
            targetColumnName,
            isRemovable,
            isIncluded: true,
          };
          newRows.push(newCombinedOption);
        }
      });

      // We cycle again the intersects looking for brand new ones, that are those
      // without a spreadsheet and its associated map already matched in the layered map
      newIntersects.forEach((intersect, i) => {
        const intersectHasNewMaps = stringObjectKeys(intersect.intersects).some(spreadsheetKey => {
          const realSpreadsheetId = extractRealSpreadsheetIdFromKey(spreadsheetKey);
          const map = getMapFromRealSpreadsheetId(maps, realSpreadsheetId);
          return map ? !map.isCurrentMap : false;
        });

        const intersectHasNewBaseMapColumn = stringObjectEntries(intersect.intersects).some(([spreadsheetKey, columnId]) => {
          const realSpreadsheetId = extractRealSpreadsheetIdFromKey(spreadsheetKey);
          const map = getMapFromRealSpreadsheetId(maps, realSpreadsheetId);
          if (map?.isCurrentMap && columnId && layeringMapInfo?.layering?.additional.new[realSpreadsheetId]?.[columnId]) {
            return true;
          }
          return false;
        });

        if (!newIntersectsAdded[i] && (intersectHasNewMaps || intersectHasNewBaseMapColumn)) {
          const spreadsheetKeys: RealSpreadsheetResponseId[] = Object.keys(intersect.intersects);
          const sourceColumnsPerMap: PerMapIdColumn = createNullsPerMap();
          let suggestedColumnName: string | null = null;
          spreadsheetKeys.forEach(spreadsheetKey => {
            if (!suggestedColumnName) {
              suggestedColumnName = getColumnDataByIntersectSpreadsheetKey({ columnData, maps, intersect, spreadsheetKey })?.name || null;
            }
            populateSourceColumnsMapWithColumnData({ columnData, maps, intersect, sourceColumnsPerMap, spreadsheetKey });
          });

          if (Object.values(sourceColumnsPerMap).some(notNullsy)) {
            const newCombinedOption: Row = {
              selectedSourceColumnPerMap: sourceColumnsPerMap,
              targetColumnName: suggestedColumnName,
              isRemovable: true,
              isIncluded: true,
            };
            newRows.push(newCombinedOption);
          }
        }
      });

      setRows(addMissingSourceColumnsToRows(newRows, sourceColumnsPerMap, maps, false));
    }
  },
  [isEditing, isConnected, sourceColumns, layeringMapInfo, columnData, maps, getMapFromRealSpreadsheetId,
    getSpreadsheetIdFromMapId, createNullsPerMap, newIntersects, sourceColumnsPerMap]);

  // This calculates the rows in creating layered map mode
  useEffect(() => {
    if (!isEditing) {
      const newRows: Row[] = [];
      for (const intersect of newIntersects) {
        const sourceColumnsPerMap = createNullsPerMap();
        const spreadsheetKeys = Object.keys(intersect.intersects);
        let suggestedColumnName: string | null = null;

        spreadsheetKeys.forEach(spreadsheetKey => {
          if (!suggestedColumnName) {
            suggestedColumnName = getColumnDataByIntersectSpreadsheetKey({ columnData, maps, intersect, spreadsheetKey })?.name || null;
          }
          populateSourceColumnsMapWithColumnData({ columnData, maps, intersect, sourceColumnsPerMap, spreadsheetKey });
        });

        const newCombinedOption: Row = {
          selectedSourceColumnPerMap: sourceColumnsPerMap,
          targetColumnName: suggestedColumnName || null,
          isRemovable: true,
          isIncluded: true,
        };
        newRows.push(newCombinedOption);
      }

      setRows(newRows);
    }
  }, [isEditing, sourceColumns, columnData, maps, getMapFromRealSpreadsheetId, createNullsPerMap, newIntersects]);

  const finalizedRows: RowWithExtraData[] = useMemo(() => rows.map(row => ({
    ...row,
    onlyOneSourceMap: hasRowOnlyOneSourceMap(row, props.maps),
    availableTargetColumnNames:
      uniqueValues(Object.values(row.selectedSourceColumnPerMap)
        .map(column => column?.name)
        .filter(notNullsy)
      ).map(columnName => ({
        name: columnName,
        value: columnName,
      })),
  })), [props.maps, rows]);

  const areAllRowsSelected = useMemo(() => !finalizedRows.some(row =>
    row.isIncluded && row.targetColumnName === null),
  [finalizedRows]);

  const firstEmptyRowIndex = useMemo(() => {
    const index = rows.findIndex(row => !row.targetColumnName);
    if (index !== -1) {
      return index;
    }
    return null;
  }, [rows]);

  const firstNewRowIndex = useMemo(() => {
    const index = rows.findIndex(row => Object.values(row.selectedSourceColumnPerMap).some((column) => (
      column && !!layeringMapInfo?.layering?.additional.new[column.realSpreadsheetId]?.[column.id]
    )));
    if (index !== -1) {
      return index;
    }
    return null;
  }, [layeringMapInfo?.layering?.additional.new, rows]);

  const toggleRowInclusion = useCallback((indexToToggle: number) => {
    setRows(rows => rows.map((row, index) => {
      if (index === indexToToggle) {
        return {
          ...row,
          isIncluded: !row.isIncluded,
        };
      }
      return row;
    }));
  }, []);

  return {
    rows: finalizedRows,
    sourceColumnsPerMap,
    onSourceColumnChange,
    sourceColumnComparator: compareRealSpreadsheetColumns,
    onRemoveRow,
    onTargetColumnNameChange,
    onAddRow,
    handleGenerateIntersectsAndSubmit,
    areAllRowsSelected,
    toggleRowInclusion,
    firstEmptyRowIndex,
    firstNewRowIndex,
  };
};
